import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import type { BankAccountFilters } from '@/features/accounting-bank/api'
import { BankAccountList } from '@/features/accounting-bank/bank-account-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the bank and cash accounts register.
 *
 * The register's search and status filter live in the URL search params so a
 * filtered view is linkable and survives a reload; `BankAccountList` matches
 * them client-side, because the endpoint takes no query parameters.
 *
 * The grant guard is presentation only. The API enforces
 * `accounting_bank.view` on every bank-account endpoint, so a stale grant
 * still 403s server-side; this keeps a user without the grant out of a screen
 * they cannot use.
 */
export const Route = createFileRoute('/_authenticated/accounting/bank-accounts/')({
  validateSearch: (search: Record<string, unknown>): BankAccountFilters => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: search.status === 'active' || search.status === 'inactive' ? search.status : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_bank.view')) {
      throw redirect({ to: '/accounting' })
    }
  },
  component: BankAccountsRoute,
})

function BankAccountsRoute() {
  const navigate = useNavigate()
  const params = Route.useSearch()

  return (
    <BankAccountList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/accounting/bank-accounts', search: next })}
    />
  )
}
